/**
 * Filter By Sports queries - React Query options and hooks
 *
 * This module provides React Query integration for the filter by sports endpoint.
 * Used for sports category navigation and filtering.
 */

import type {
    QueryClient,
    UseQueryOptions,
    UseQueryResult,
    UseSuspenseQueryOptions,
    UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { fetchFilterBySports } from 'app-migration/api/api';
import type { FilterBySportsCompetition, FilterBySportsItem, FilterBySportsParams } from 'models/search';
import { throwIfUpstreamUnavailable } from 'utils/cachedRenderGuard';
import logProviderFactory from 'utils/logger/logProviderFactory';

/* -------------------------------------------------------------------------- */
/*                          TYPES                                             */
/* -------------------------------------------------------------------------- */

/**
 * Response structure for filter by sports query
 * Contains filters keyed by sport name, plus optional ordering array
 */
export interface FilterBySportsData {
    filters: Record<string, FilterBySportsItem>;
    ordering?: string[];
}

/**
 * Empty result for error fallbacks
 */
export const EMPTY_FILTER_BY_SPORTS: FilterBySportsData = {
    filters: {},
    ordering: undefined,
};

/* -------------------------------------------------------------------------- */
/*                          HELPERS                                           */
/* -------------------------------------------------------------------------- */

/**
 * Extracts per-scope counts from scopes when they come as Record<string, {count}>.
 * Returns undefined if scopes are already a plain string[].
 */
export function extractScopeCounts(scopes: string[] | Record<string, unknown>): Record<string, number> | undefined {
    if (Array.isArray(scopes)) {
        return undefined;
    }
    const counts: Record<string, number> = {};
    for (const [key, value] of Object.entries(scopes)) {
        if (
            value &&
            typeof value === 'object' &&
            'count' in value &&
            typeof (value as { count: number }).count === 'number'
        ) {
            counts[key] = (value as { count: number }).count;
        }
    }
    return counts;
}

/**
 * Returns the total count by summing per-scope counts.
 */
function sumScopeCounts(scopeCounts: Record<string, number> | undefined): number | undefined {
    if (!scopeCounts) return undefined;
    let total = 0;
    for (const count of Object.values(scopeCounts)) {
        total += count;
    }
    return total;
}

/**
 * Normalizes scopes from the API response.
 * When with_counts=true, scopes come back as Record<string, {count}> instead of string[].
 * This converts them back to string[], using scopeOrdering to preserve the intended order.
 */
export function normalizeScopes(scopes: string[] | Record<string, unknown>, scopeOrdering?: string[]): string[] {
    if (Array.isArray(scopes)) {
        return scopes;
    }
    const keys = Object.keys(scopes);
    if (!scopeOrdering || scopeOrdering.length === 0) {
        return keys;
    }
    // Sort keys by their position in scopeOrdering; unknown keys go at the end
    return keys.toSorted((a, b) => {
        const idxA = scopeOrdering.indexOf(a);
        const idxB = scopeOrdering.indexOf(b);
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
    });
}

/**
 * Normalizes a FilterBySportsItem, ensuring scopes are string[] and
 * sorting competitions by ranking_score descending, falling back to recent_volume.
 */
export function normalizeFilter(filter: FilterBySportsItem, scopeOrdering?: string[]): FilterBySportsItem {
    const normalizedCompetitions: Record<string, FilterBySportsCompetition> = {};
    const entries = Object.entries(filter.competitions);
    const competitionRank = (competition: FilterBySportsCompetition) =>
        competition.ranking_score ?? competition.recent_volume ?? 0;
    entries.sort(([, a], [, b]) => competitionRank(b) - competitionRank(a));
    for (const [key, comp] of entries) {
        const compScopeCounts = extractScopeCounts(comp.scopes) ?? comp.scopeCounts;
        normalizedCompetitions[key] = {
            ...comp,
            scopes: normalizeScopes(comp.scopes, scopeOrdering),
            scopeCounts: compScopeCounts,
            count: sumScopeCounts(compScopeCounts) ?? comp.count,
        };
    }
    const sportScopeCounts = extractScopeCounts(filter.scopes) ?? filter.scopeCounts;
    return {
        ...filter,
        scopes: normalizeScopes(filter.scopes, scopeOrdering),
        scopeCounts: sportScopeCounts,
        competitions: normalizedCompetitions,
        count: sumScopeCounts(sportScopeCounts) ?? filter.count,
    };
}

/**
 * Sorts filters by the provided ordering, keeping only filters with scopes.
 * If ordering has fewer than 3 items, returns filters unchanged.
 * Also normalizes scopes and sorts competitions by recent volume.
 */
export function sortFiltersByOrdering(
    filters: Record<string, FilterBySportsItem>,
    ordering?: string[],
    scopeOrdering?: string[],
): Record<string, FilterBySportsItem> {
    const keys = ordering && ordering.length > 2 ? ordering : Object.keys(filters);

    const sortedFilters: Record<string, FilterBySportsItem> = {};
    keys.forEach((sport) => {
        const filter = filters[sport];
        if (filter) {
            const normalized = normalizeFilter(filter, scopeOrdering);
            if (normalized.scopes.length > 0) {
                sortedFilters[sport] = normalized;
            }
        }
    });
    return sortedFilters;
}

/* -------------------------------------------------------------------------- */
/*                          QUERY KEYS                                        */
/* -------------------------------------------------------------------------- */

/**
 * Query key factory for filter by sports queries
 */
export const filterBySportsKeys = {
    all: ['filterBySports'] as const,
    list: (params?: FilterBySportsParams) => [...filterBySportsKeys.all, params ?? {}] as const,
};

/* -------------------------------------------------------------------------- */
/*                          QUERY OPTIONS                                     */
/* -------------------------------------------------------------------------- */

/**
 * Query options factory for filter by sports
 */
export const filterBySportsOptions = (params?: FilterBySportsParams) =>
    queryOptions<FilterBySportsData>({
        queryKey: filterBySportsKeys.list(params),
        queryFn: async () => {
            const data = await fetchFilterBySports();
            if (!data) {
                logProviderFactory.logError('Failed to fetch filter by sports: Response not ok');
                return EMPTY_FILTER_BY_SPORTS;
            }
            const { filters_by_sports, sport_ordering, scope_ordering } = data;
            return {
                filters: sortFiltersByOrdering(filters_by_sports, sport_ordering, scope_ordering),
                ordering: sport_ordering,
            };
        },
        staleTime: 2 * 60 * 1000, // 2 minutes to match server revalidation
        gcTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
        placeholderData: (previousData) => previousData, // Keep previous data during refetch
    });

/* -------------------------------------------------------------------------- */
/*                          QUERY HOOKS                                       */
/* -------------------------------------------------------------------------- */

/**
 * Hook to get filter by sports data.
 * Uses Suspense - must be used inside a Suspense boundary.
 *
 * @param params - Optional params (e.g., milestone_id)
 * @param options - Additional query options
 */
export function useFilterBySports(
    params?: FilterBySportsParams,
    options?: Partial<Omit<UseSuspenseQueryOptions<FilterBySportsData>, 'queryKey' | 'queryFn'>>,
): UseSuspenseQueryResult<FilterBySportsData> {
    return useSuspenseQuery({
        ...filterBySportsOptions(params),
        ...options,
    });
}

/**
 * Non-suspense variant of useFilterBySports.
 * Safe for use in leaf components without a Suspense boundary.
 * Returns `undefined` while the data loads (no suspension, no throw).
 *
 * @param params - Optional params (e.g., milestone_id)
 * @param options - Additional query options
 */
export function useFilterBySportsQuery(
    params?: FilterBySportsParams,
    options?: Partial<Omit<UseQueryOptions<FilterBySportsData>, 'queryKey' | 'queryFn'>>,
): UseQueryResult<FilterBySportsData> {
    return useQuery({
        ...filterBySportsOptions(params),
        ...options,
    });
}

export interface PrefetchFilterBySportsOptions {
    /**
     * Propagate an upstream failure instead of degrading to
     * `EMPTY_FILTER_BY_SPORTS`.
     *
     * Cached (ISR) callers that treat empty filters as real absence MUST pass
     * this. `/category/sports` is the canonical case: with no filter keys it
     * calls `notFound()`, and on a cached route that failure-derived 404 is
     * stored as the *successful* response for the whole revalidate window.
     * Throwing instead makes Next discard the render — a failed background
     * revalidation keeps the last good entry, and a failed first generation
     * surfaces an uncached error that the next request retries. Suppressed
     * during `next build` (see `throwIfUpstreamUnavailable`).
     *
     * Leave it off where empty filters degrade gracefully (the sports layout's
     * nav, the sports leaf pages' `hasFilterData` gate, the home/calendar
     * prefetches) — an empty section beats failing the page.
     */
    throwOnFetchError?: boolean;
}

/**
 * Prefetches filter by sports data for SSR.
 * Uses fetchFilterBySports for server-side fetching and applies the same sorting logic.
 *
 * @param opts - See `PrefetchFilterBySportsOptions`
 */
export async function prefetchFilterBySports(opts: PrefetchFilterBySportsOptions = {}): Promise<FilterBySportsData> {
    // Strict while prerendering too — same reasoning as throwIfUpstreamUnavailable: a
    // prerender is the first cached response, so swallowing ships the failure-derived page.
    const shouldThrowOnFetchError = !!opts.throwOnFetchError;

    try {
        const data = await fetchFilterBySports();
        const upstreamUnavailable = !data;
        if (upstreamUnavailable) {
            logProviderFactory.logError('Failed to prefetch filter by sports: Response not ok');
        }
        // Pass the condition, not the opt-in flag: `throwIfUpstreamUnavailable` documents
        // its first argument as "upstream was unavailable", and reading the flag there
        // inverts that contract for anyone tracing the call.
        if (shouldThrowOnFetchError) {
            throwIfUpstreamUnavailable(upstreamUnavailable, 'Sports filters');
        }
        if (!data) return EMPTY_FILTER_BY_SPORTS;

        const { filters_by_sports, sport_ordering, scope_ordering } = data;
        return {
            filters: sortFiltersByOrdering(filters_by_sports, sport_ordering, scope_ordering),
            ordering: sport_ordering,
        };
    } catch (error) {
        // Already logged above when the response itself was the problem; rethrow
        // so the caller's cached render is discarded rather than persisting a
        // failure-derived 404.
        if (shouldThrowOnFetchError) throw error;
        logProviderFactory.logError(`Failed to prefetch filter by sports: ${error}`);
        return EMPTY_FILTER_BY_SPORTS;
    }
}

/**
 * Sets filter by sports data directly into the query cache.
 * Use this when you already have the data from a page fetch and want to hydrate the cache.
 *
 * @param queryClient - React Query client
 * @param data - The filter by sports data to cache
 * @param params - Optional params to match the query key
 */
export function setFilterBySportsData(
    queryClient: QueryClient,
    data: FilterBySportsData,
    params?: FilterBySportsParams,
) {
    queryClient.setQueryData(filterBySportsKeys.list(params), data);
}
